import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FeatureCard from '../components/FeatureCard';
import { FEATURE_GROUPS } from '../data/features';
import Lottie from 'lottie-react';
import practiceAnim from '../lottie/practice.json';
import memorizeAnim from '../lottie/memorize.json';
import favoritesAnim from '../lottie/favorites.json';
import { useUserData } from '../contexts/UserDataContext';
import { useLanguageToggle } from '../contexts/LanguageToggleContext';
import { api } from '../services/api';
import ItemCard from '../components/ItemCard';
import headerLogo from '../components/header.json';
import { translations } from '../translations';

// Helper function to get translation with fallback
const getTranslation = (selectedLanguage: string, key: string, fallbackKey?: string) => {
  const langTranslations = translations[selectedLanguage as keyof typeof translations];
  if (langTranslations && 'homePage' in langTranslations) {
    const homePage = (langTranslations as any).homePage;
    if (homePage && key in homePage) {
      return homePage[key];
    }
  }
  // Fallback to English
  const englishTranslations = translations.en;
  if (fallbackKey) {
    return (englishTranslations as any).homePage[fallbackKey] || (englishTranslations as any).homePage[key];
  }
  return (englishTranslations as any).homePage[key];
};

const featureExamples = {
  Practice: (
    <div className="bg-white/80 rounded-2xl shadow-xl p-6 w-80 animate-fade-in">
      <div className="text-blue-700 font-bold mb-2">Example:</div>
      <div className="text-lg font-semibold mb-1">Translate: <span className="text-indigo-700">"She runs fast."</span></div>
      <div className="text-gray-700 mb-2">Your answer: <span className="italic text-gray-500">Type here...</span></div>
      <div className="text-green-600 font-bold">Correct!</div>
    </div>
  ),
  Memorize: (
    <div className="bg-white/80 rounded-2xl shadow-xl p-6 w-80 animate-fade-in">
      <div className="text-blue-700 font-bold mb-2">Example:</div>
      <div className="text-lg font-semibold mb-1">Memorize: <span className="text-indigo-700">"I am happy."</span></div>
      <div className="text-gray-700 mb-2">Translation: <span className="italic text-gray-500">मैं खुश हूँ।</span></div>
      <div className="text-yellow-600 font-bold">Add to Favorites ⭐</div>
    </div>
  ),
  Favorites: (
    <div className="bg-white/80 rounded-2xl shadow-xl p-6 w-80 animate-fade-in">
      <div className="text-blue-700 font-bold mb-2">Example:</div>
      <div className="text-lg font-semibold mb-1">Favorite Word: <span className="text-indigo-700">"Benevolent"</span></div>
      <div className="text-gray-700 mb-2">Meaning: <span className="italic text-gray-500">Well meaning and kindly</span></div>
      <div className="text-yellow-600 font-bold">Remove from Favorites ❌</div>
    </div>
  ),
  Completed: (
    <div className="bg-white/80 rounded-2xl shadow-xl p-6 w-80 animate-fade-in">
      <div className="text-blue-700 font-bold mb-2">Example:</div>
      <div className="text-lg font-semibold mb-1">Completed Sentence: <span className="text-indigo-700">"This is a pen."</span></div>
      <div className="text-gray-700 mb-2">Translation: <span className="italic text-gray-500">यह एक पेन है।</span></div>
      <div className="text-green-600 font-bold">Well done! 🎉</div>
    </div>
  ),
};

// Add this new object for matching examples
const featureTypeExamples: any = {
  'Practice': [
    {
      title: 'Grammar Practice',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Noun: <span className="text-indigo-700">"Ram is a boy."</span></div>
          <div className="text-gray-700 mb-1">Explanation: <span className="italic text-gray-500">Ram is the subject (noun).</span></div>
        </div>
      )
    },
    {
      title: 'Sentence Practice',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Translate: <span className="text-indigo-700">"She runs fast."</span></div>
          <div className="text-gray-700 mb-1">Your answer: <span className="italic text-gray-500">Type here...</span></div>
        </div>
      )
    },
    {
      title: 'Word Practice',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Word: <span className="text-indigo-700">"Benevolent"</span></div>
          <div className="text-gray-700 mb-1">Meaning: <span className="italic text-gray-500">Well meaning and kindly</span></div>
        </div>
      )
    },
  ],
  'Memorize': [
    {
      title: 'Memorize Grammar',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Verb: <span className="text-indigo-700">"She runs fast."</span></div>
          <div className="text-gray-700 mb-1">Explanation: <span className="italic text-gray-500">"runs" is the verb.</span></div>
        </div>
      )
    },
    {
      title: 'Memorize Sentences',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Memorize: <span className="text-indigo-700">"I am happy."</span></div>
          <div className="text-gray-700 mb-1">Translation: <span className="italic text-gray-500">मैं खुश हूँ।</span></div>
        </div>
      )
    },
    {
      title: 'Memorize Words',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Word: <span className="text-indigo-700">"Opportunity"</span></div>
          <div className="text-gray-700 mb-1">Meaning: <span className="italic text-gray-500">A set of circumstances that makes it possible to do something.</span></div>
        </div>
      )
    },
  ],
  'Favorites': [
    {
      title: 'Favorite Grammar',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Grammar: <span className="text-indigo-700">"Verb"</span></div>
          <div className="text-gray-700 mb-1">Explanation: <span className="italic text-gray-500">A verb expresses action.</span></div>
        </div>
      )
    },
    {
      title: 'Favorite Sentences',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Favorite Sentence: <span className="text-indigo-700">"This is a pen."</span></div>
          <div className="text-gray-700 mb-1">Translation: <span className="italic text-gray-500">यह एक पेन है।</span></div>
        </div>
      )
    },
    {
      title: 'Favorite Words',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Favorite Word: <span className="text-indigo-700">"Benevolent"</span></div>
          <div className="text-gray-700 mb-1">Meaning: <span className="italic text-gray-500">Well meaning and kindly</span></div>
        </div>
      )
    },
  ],
  'Completed': [
    {
      title: 'Completed Grammar',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Grammar: <span className="text-indigo-700">"Adjective"</span></div>
          <div className="text-gray-700 mb-1">Explanation: <span className="italic text-gray-500">An adjective describes a noun.</span></div>
        </div>
      )
    },
    {
      title: 'Completed Sentences',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Completed Sentence: <span className="text-indigo-700">"This is a pen."</span></div>
          <div className="text-gray-700 mb-1">Translation: <span className="italic text-gray-500">यह एक पेन है।</span></div>
        </div>
      )
    },
    {
      title: 'Completed Words',
      example: (
        <div className="bg-white/80 rounded-2xl shadow-xl p-4 w-64 animate-fade-in">
          <div className="text-blue-700 font-bold mb-1">Example:</div>
          <div className="text-base font-semibold mb-1">Completed Word: <span className="text-indigo-700">"Achievement"</span></div>
          <div className="text-gray-700 mb-1">Meaning: <span className="italic text-gray-500">A thing done successfully, typically by effort, courage, or skill.</span></div>
        </div>
      )
    },
  ],
};

// Change featureColors to all use 'bg-white/80 backdrop-blur-xl' for pastel glassmorphism look
const featureColors = [
  'bg-white/80 backdrop-blur-xl',
  'bg-white/80 backdrop-blur-xl',
  'bg-white/80 backdrop-blur-xl',
  'bg-white/80 backdrop-blur-xl',
  'bg-white/80 backdrop-blur-xl',
];

const featureIcons = [
  '📝', // Practice
  '🤖', // AI Teacher
  '🧠', // Memorize
  '⭐', // Favorites
  '✅', // Completed
];

const featureTitles = ['Learn English', 'AI English Teacher', 'Memorize', 'Favorites', 'Completed'];

const featureDescriptions = [
  <>
    <div className="mb-2">
      Unlock the full potential of your English learning journey:
    </div>
    <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
      <li>Practice grammar and sentences to master rules and real-world usage.</li>
      <li>Sentences are organized by category, helping you learn in context.</li>
      <li>Expand your vocabulary with words from beginner to intermediate levels.</li>
      <li>Practice real-life English with interactive conversations on various topics.</li>
    </ul>
    <div className="mb-2">
      For each item, you have three options:
    </div>
    <ul className="list-decimal list-inside mb-2 text-base text-gray-800 pl-4">
      <li><b>Done:</b> Mark as completed if you already know it or have finished learning.</li>
      <li><b>Memorize:</b> Add to your memorize list to review until you're confident.</li>
      <li><b>Favorite:</b> Save to your favorites for quick access anytime.</li>
    </ul>
    <div>
      These tools help you track progress, reinforce learning, and build a personalized study path.
    </div>
  </>,
  <>
    <div className="mb-2">
      {/* This will be replaced with translations in the component */}
    </div>
    <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
      {/* This will be replaced with translations in the component */}
    </ul>
    <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
      <div className="text-sm font-semibold text-blue-800 mb-1">
        {/* This will be replaced with translations in the component */}
      </div>
      <div className="text-xs text-gray-700">
        <div><strong>You:</strong> I going to store</div>
        <div><strong>AI Teacher:</strong> Actually, it should be 'I am going to the store' instead of 'I going to store'. You need 'am' for present continuous tense and 'the' article before 'store'. What do you need to buy at the store?</div>
      </div>
    </div>
  </>,
  <>
    <div className="mb-2">
      Memorize helps you remember sentences and words that you find important or difficult.
    </div>
    <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
      <li>See all the sentences and words you've chosen to memorize in one place.</li>
      <li>Each item has three buttons:
        <ul className="list-decimal list-inside ml-4">
          <li><b>Done:</b> Mark as completed when you've mastered it.</li>
          <li><b>Memorize Again:</b> Review the item again for better retention.</li>
          <li><b>Favorite:</b> Add to your favorites for quick access.</li>
        </ul>
      </li>
      <li>The English version is hidden by default; click the 'View' button to reveal it.</li>
      <li>Try writing the answer yourself in the text box and get instant feedback on your attempt.</li>
    </ul>
    <div>
      This section is designed to reinforce your learning and help you gain confidence through active recall and repetition.
    </div>
  </>,
  <>
    <div className="mb-2">
      Here you'll find all the sentences and words you've marked as favorite.
    </div>
    <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
      <li>This is the best place to review your personal favorites list at any time.</li>
      <li>Use the <b>Unfavorite</b> button to remove items from your list whenever you wish.</li>
    </ul>
    <div>
      Keep your most important and useful content just a click away for quick revision and practice.
    </div>
  </>,
  <>
    <div className="mb-2">
      Congratulations on your progress! Here you can see all the grammar topics, sentences, and words you've completed.
    </div>
    <ul className="list-disc list-inside mb-2 text-base text-green-800 pl-4">
      <li>Celebrate your achievements and see how far you've come.</li>
      <li>Review completed items to reinforce your learning and boost your confidence.</li>
      <li>Keep going—every completed item brings you closer to mastering English!</li>
    </ul>
    <div>
      Stay motivated and keep up the great work. Your English journey is just getting better!
    </div>
  </>,
];

const featureSubFeatures = [
  [
    'Grammar Practice: Master rules with examples',
    'Sentence Practice: Translate and build sentences',
    'Word Practice: Expand your vocabulary',
    'Conversations: Practice real-life English',
  ],
  [
    'Real-time Grammar Correction: Get instant feedback',
    'Conversational Practice: Voice interactions',
    'Personalized Learning: Adapts to your level',
    '3D Avatar: Interact with Professor Alex',
  ],
  [
    'Memorize Grammar: Learn rules and examples',
    'Memorize Sentences: Useful daily phrases',
    'Memorize Words: Important vocabulary',
  ],
  [
    'Favorite Grammar: Save key topics',
    'Favorite Sentences: Save useful phrases',
    'Favorite Words: Save important words',
  ],
  [
    'Completed Grammar: Track finished topics',
    'Completed Sentences: Review what you mastered',
    'Completed Words: See your vocabulary growth',
  ],
];

// Replace animatedSvgs with lottieAnimations
const lottieAnimations = [
  practiceAnim,
  memorizeAnim,
  favoritesAnim,
  favoritesAnim,
];

const whyGuru = [
  { icon: '🚀', title: 'Fast Progress', desc: 'Smart cards and spaced repetition help you learn faster.' },
  { icon: '🌏', title: 'Multi-Language', desc: 'Supports English, Hindi, Bengali, Marathi, Telugu, Tamil, Gujarati.' },
  { icon: '📈', title: 'Track Progress', desc: 'See your growth and stay motivated with visual stats.' },
  { icon: '💡', title: 'Smart Practice', desc: 'Instant feedback and personalized review.' },
  { icon: '🔒', title: 'Safe & Private', desc: 'Your data is secure and never shared.' },
];

// Sample data for realistic ItemCard examples
export const sampleGrammar = {
  sentence: 'Birds sing.',
  en: 'Birds sing.',
  hi: 'पक्षी गाते हैं।',
  bn: 'পাখিরা গান গায়।',
  mr: 'पक्षी गातात.',
  te: 'పక్షులు పాడుతాయి.',
  ta: 'பறவைகள் பாடுகின்றன.',
  gu: 'પક્ષીઓ ગાય છે.',
  category: 'Subject-Verb Agreement'
};
export const sampleSentence = {
  sentence: 'She runs fast.',
  en: 'She runs fast.',
  hi: 'वह तेज दौड़ती है।',
  bn: 'সে দ্রুত দৌড়ায়।',
  mr: 'ती वेगाने धावते.',
  te: 'ఆమె వేగంగా పరుగెత్తుతుంది.',
  ta: 'அவள் விரைவாக ஓடுகிறாள்.',
  gu: 'તે ઝડપથી દોડે છે.',
  category: '3 words'
};
export const sampleWord = {
  word: 'Benevolent',
  en: 'Benevolent',
  hi: 'दयालु',
  bn: 'উদার',
  mr: 'दयाळू',
  te: 'ఉదారమైన',
  ta: 'கருணையுள்ள',
  gu: 'દયાળુ',
  meaning: 'Well meaning and kindly',
  sentence: '', // add to satisfy ItemCard type
  category: ''  // add to satisfy ItemCard type
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { userData } = useUserData();
  const userId = currentUser?.uid;
  const selectedLanguage = userData?.selectedLanguage || 'en';
  const { showEnglish, setShowEnglish } = useLanguageToggle();

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
    { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
    { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
    { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
    { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
    { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  ];

  // Memoized translations for performance
  const memoizedTranslations = React.useMemo(() => ({
    heroSubtitle: showEnglish ? getTranslation('en', 'heroSubtitle') : getTranslation(selectedLanguage, 'heroSubtitle'),
    exploreFeatures: showEnglish ? getTranslation('en', 'exploreFeatures') : getTranslation(selectedLanguage, 'exploreFeatures'),
    unlockTitle: showEnglish ? getTranslation('en', 'unlockTitle') : getTranslation(selectedLanguage, 'unlockTitle'),
    unlockList1: showEnglish ? getTranslation('en', 'unlockList1') : getTranslation(selectedLanguage, 'unlockList1'),
    unlockList2: showEnglish ? getTranslation('en', 'unlockList2') : getTranslation(selectedLanguage, 'unlockList2'),
    unlockList3: showEnglish ? getTranslation('en', 'unlockList3') : getTranslation(selectedLanguage, 'unlockList3'),
    unlockList4: showEnglish ? getTranslation('en', 'unlockList4') : getTranslation(selectedLanguage, 'unlockList4'),
    unlockList5: showEnglish ? getTranslation('en', 'unlockList5') : getTranslation(selectedLanguage, 'unlockList5'),
    unlockList6: showEnglish ? getTranslation('en', 'unlockList6') : getTranslation(selectedLanguage, 'unlockList6'),
    unlockList7: showEnglish ? getTranslation('en', 'unlockList7') : getTranslation(selectedLanguage, 'unlockList7'),
    unlockList8: showEnglish ? getTranslation('en', 'unlockList8') : getTranslation(selectedLanguage, 'unlockList8'),
    unlockList9: showEnglish ? getTranslation('en', 'unlockList9') : getTranslation(selectedLanguage, 'unlockList9'),
    memorizeTitle: showEnglish ? getTranslation('en', 'memorizeTitle') : getTranslation(selectedLanguage, 'memorizeTitle'),
    memorizeDesc1: showEnglish ? getTranslation('en', 'memorizeDesc1') : getTranslation(selectedLanguage, 'memorizeDesc1'),
    memorizeDesc2: showEnglish ? getTranslation('en', 'memorizeDesc2') : getTranslation(selectedLanguage, 'memorizeDesc2'),
    memorizeDesc3: showEnglish ? getTranslation('en', 'memorizeDesc3') : getTranslation(selectedLanguage, 'memorizeDesc3'),
    memorizeDesc4: showEnglish ? getTranslation('en', 'memorizeDesc4') : getTranslation(selectedLanguage, 'memorizeDesc4'),
    memorizeDesc5: showEnglish ? getTranslation('en', 'memorizeDesc5') : getTranslation(selectedLanguage, 'memorizeDesc5'),
    memorizeDesc6: showEnglish ? getTranslation('en', 'memorizeDesc6') : getTranslation(selectedLanguage, 'memorizeDesc6'),
    memorizeDesc7: showEnglish ? getTranslation('en', 'memorizeDesc7') : getTranslation(selectedLanguage, 'memorizeDesc7'),
    memorizeDesc8: showEnglish ? getTranslation('en', 'memorizeDesc8') : getTranslation(selectedLanguage, 'memorizeDesc8'),
    favoriteTitle: showEnglish ? getTranslation('en', 'favoriteTitle') : getTranslation(selectedLanguage, 'favoriteTitle'),
    favoriteDesc1: showEnglish ? getTranslation('en', 'favoriteDesc1') : getTranslation(selectedLanguage, 'favoriteDesc1'),
    favoriteDesc2: showEnglish ? getTranslation('en', 'favoriteDesc2') : getTranslation(selectedLanguage, 'favoriteDesc2'),
    favoriteDesc3: showEnglish ? getTranslation('en', 'favoriteDesc3') : getTranslation(selectedLanguage, 'favoriteDesc3'),
    completedTitle: showEnglish ? getTranslation('en', 'completedTitle') : getTranslation(selectedLanguage, 'completedTitle'),
    completedDesc1: showEnglish ? getTranslation('en', 'completedDesc1') : getTranslation(selectedLanguage, 'completedDesc1'),
    completedDesc2: showEnglish ? getTranslation('en', 'completedDesc2') : getTranslation(selectedLanguage, 'completedDesc2'),
    completedDesc3: showEnglish ? getTranslation('en', 'completedDesc3') : getTranslation(selectedLanguage, 'completedDesc3'),
    completedDesc4: showEnglish ? getTranslation('en', 'completedDesc4') : getTranslation(selectedLanguage, 'completedDesc4'),
    whyGuruTitle: showEnglish ? getTranslation('en', 'whyGuruTitle') : getTranslation(selectedLanguage, 'whyGuruTitle'),
    whyGuruDesc1: showEnglish ? getTranslation('en', 'whyGuruDesc1') : getTranslation(selectedLanguage, 'whyGuruDesc1'),
    whyGuruDesc2: showEnglish ? getTranslation('en', 'whyGuruDesc2') : getTranslation(selectedLanguage, 'whyGuruDesc2'),
    whyGuruDesc3: showEnglish ? getTranslation('en', 'whyGuruDesc3') : getTranslation(selectedLanguage, 'whyGuruDesc3'),
    whyGuruDesc4: showEnglish ? getTranslation('en', 'whyGuruDesc4') : getTranslation(selectedLanguage, 'whyGuruDesc4'),
    whyGuruDesc5: showEnglish ? getTranslation('en', 'whyGuruDesc5') : getTranslation(selectedLanguage, 'whyGuruDesc5'),
    // AI English Teacher translations
    aiTeacherTitle: showEnglish ? getTranslation('en', 'aiTeacherTitle') : getTranslation(selectedLanguage, 'aiTeacherTitle'),
    aiTeacherRealTimeGrammar: showEnglish ? getTranslation('en', 'aiTeacherRealTimeGrammar') : getTranslation(selectedLanguage, 'aiTeacherRealTimeGrammar'),
    aiTeacherConversationalPractice: showEnglish ? getTranslation('en', 'aiTeacherConversationalPractice') : getTranslation(selectedLanguage, 'aiTeacherConversationalPractice'),
    aiTeacherPersonalizedLearning: showEnglish ? getTranslation('en', 'aiTeacherPersonalizedLearning') : getTranslation(selectedLanguage, 'aiTeacherPersonalizedLearning'),
    aiTeacherEncouragingSupport: showEnglish ? getTranslation('en', 'aiTeacherEncouragingSupport') : getTranslation(selectedLanguage, 'aiTeacherEncouragingSupport'),
    aiTeacherVoiceRecognition: showEnglish ? getTranslation('en', 'aiTeacherVoiceRecognition') : getTranslation(selectedLanguage, 'aiTeacherVoiceRecognition'),
    aiTeacher3DAvatar: showEnglish ? getTranslation('en', 'aiTeacher3DAvatar') : getTranslation(selectedLanguage, 'aiTeacher3DAvatar'),
    aiTeacherExampleConversation: showEnglish ? getTranslation('en', 'aiTeacherExampleConversation') : getTranslation(selectedLanguage, 'aiTeacherExampleConversation'),
  }), [selectedLanguage, showEnglish]);

  // Debug logs
  console.log('HomePage currentUser:', currentUser);
  console.log('HomePage userId:', userId);
  console.log('HomePage FEATURE_GROUPS:', FEATURE_GROUPS);

  // Progress state (copied from UserDashboard)
  const [progress, setProgress] = React.useState({
    grammar: { completed: 0, total: 0 },
    sentences: { completed: 0, total: 0 },
    words: { completed: 0, total: 0 },
  });
  const [isLoadingProgress, setIsLoadingProgress] = React.useState(false);

  React.useEffect(() => {
    if (!userId) return;

    setIsLoadingProgress(true);
    // Fetch real progress data from API
    Promise.all([
      api.getMasterGrammarCount(''),
      api.getCompletedGrammarCount(userId, ''),
      api.getMasterWordsCount(''),
      api.getCompletedWordsCount(userId, ''),
      api.getSentenceProgress(userId)
    ]).then(([totalGrammar, completedGrammar, totalWords, completedWords, sentenceProgress]) => {
      setProgress({
        grammar: { completed: completedGrammar, total: totalGrammar },
        sentences: { completed: sentenceProgress.completed, total: sentenceProgress.total },
        words: { completed: completedWords, total: totalWords },
      });
    }).catch((error) => {
      console.error('Error fetching progress data:', error);
      // Keep the sample data as fallback if API fails
    }).finally(() => {
      setIsLoadingProgress(false);
    });
  }, [userId]);

  // ProgressBar component (updated for compact display)
  function ProgressBarFancy({ label, completed, total, isLoading = false }: { label: string, completed: number, total: number, isLoading?: boolean }) {
    const percent = total > 0 ? Math.round((completed / total) * 100) : (completed > 0 ? 100 : 0);
    // Choose color and icon based on label
    let colorFrom = 'from-green-400';
    let colorTo = 'to-green-600';
    let icon = '📚';
    let labelColor = 'text-green-700';
    let percentColor = 'bg-gradient-to-r from-green-500 via-green-400 to-green-600 bg-clip-text text-transparent';
    let glow = 'shadow-green-300/60';
    let progressLabel = 'Progress';
    if (label.toLowerCase().includes('grammar')) {
      colorFrom = 'from-blue-400';
      colorTo = 'to-blue-600';
      icon = '📖';
      labelColor = 'text-blue-700';
      percentColor = 'bg-gradient-to-r from-blue-500 via-blue-400 to-blue-700 bg-clip-text text-transparent';
      glow = 'shadow-blue-300/60';
      progressLabel = 'Grammar Progress';
    } else if (label.toLowerCase().includes('sentence')) {
      colorFrom = 'from-pink-400';
      colorTo = 'to-pink-600';
      icon = '✍️';
      labelColor = 'text-pink-700';
      percentColor = 'bg-gradient-to-r from-pink-500 via-pink-400 to-pink-700 bg-clip-text text-transparent';
      glow = 'shadow-pink-300/60';
      progressLabel = 'Sentence Progress';
    } else if (label.toLowerCase().includes('word')) {
      colorFrom = 'from-yellow-300';
      colorTo = 'to-yellow-500';
      icon = '🔤';
      labelColor = 'text-yellow-700';
      percentColor = 'bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-700 bg-clip-text text-transparent';
      glow = 'shadow-yellow-200/60';
      progressLabel = 'Word Progress';
    }
    return (
      <div className="flex flex-col items-center w-full max-w-sm px-2 mb-3 md:mb-0">
        <span className="text-xs font-semibold text-white mb-1 drop-shadow-sm">{progressLabel}</span>
        <div className="relative w-full h-4 bg-green-100 border border-green-200 rounded-full shadow-md overflow-hidden">
          <div
            className={`absolute left-0 top-0 h-full rounded-full bg-gradient-to-r ${colorFrom} via-white/60 ${colorTo} animate-progress-shimmer ${glow}`}
            style={{ width: `${percent}%`, minWidth: percent > 0 ? '16px' : '0', transition: 'width 0.7s cubic-bezier(.4,2,.6,1)' }}
          ></div>
          {/* Small percentage overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className={`text-xs font-bold drop-shadow-sm ${percentColor}`}>
              {isLoading ? '...' : `${percent}%`}
            </span>
          </div>
        </div>
        {/* Progress details */}
        <div className="text-xs text-white/80 mt-1 text-center">
          {isLoading ? 'Loading...' : `${completed}/${total} completed`}
        </div>
      </div>
    );
  }

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  // Add animated gradient overlay and floating shapes for a vibrant, modern look
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-blue-500 via-pink-400 to-yellow-300 flex flex-col">
      {/* Animated gradient overlay */}
      {/* <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[60vw] h-[60vw] bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400 opacity-50 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] bg-gradient-to-br from-yellow-300 via-pink-200 to-blue-200 opacity-40 rounded-full blur-3xl animate-float-slower" />
        <div className="absolute top-[30%] right-[-5%] w-[30vw] h-[30vw] bg-gradient-to-br from-pink-400 via-purple-300 to-blue-300 opacity-40 rounded-full blur-2xl animate-float" />
      </div> */}
      {/* Main content */}
      <div className="relative z-10 flex-grow">
        {/* Hero Section */}
        <section className="max-w-2xl sm:max-w-5xl mx-auto pt-8 sm:pt-16 pb-6 sm:pb-10 px-2 sm:px-4 text-center animate-fade-in">
          <div className="relative inline-block w-full bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 rounded-2xl sm:rounded-3xl shadow-2xl px-4 sm:px-10 py-8 sm:py-20 mb-6 sm:mb-12 border-2 sm:border-4 border-yellow-300/80 overflow-hidden">
            {/* Language Toggle Button - Hide when English is selected */}
            {selectedLanguage && selectedLanguage !== 'en' && (
              <div className="absolute top-4 right-4 z-20">
                <button
                  className={`relative flex items-center px-0.5 py-0.5 rounded-full border-2 transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 overflow-hidden`
                    + (showEnglish ? ' bg-gradient-to-r from-blue-500 via-purple-400 to-pink-400 border-blue-400' : ' bg-white border-blue-200')}
                  style={{ minWidth: 120, height: 32, fontSize: 11 }}
                  onClick={() => setShowEnglish(!showEnglish)}
                  aria-label={showEnglish ? `Show in ${languages.find(l => l.code === selectedLanguage)?.name || 'Selected Language'}` : 'Show in English'}
                >
                  <span
                    className={`z-10 px-3 py-0.5 text-xs font-semibold rounded-full transition-colors duration-200`
                      + (showEnglish ? ' text-white' : ' text-blue-700')}
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    English
                  </span>
                  <span
                    className={`z-10 px-3 py-0.5 text-xs font-semibold rounded-full transition-colors duration-200`
                      + (!showEnglish ? ' text-white' : ' text-blue-700')}
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    {languages.find(l => l.code === selectedLanguage)?.name || selectedLanguage}
                  </span>
                  {/* Animated pill background */}
                  <span
                    className={`absolute top-0 left-0 h-full w-1/2 rounded-full transition-all duration-200 pointer-events-none`
                      + ' bg-gradient-to-r from-blue-500 via-purple-400 to-pink-400'}
                    style={{
                      width: '50%',
                      transform: showEnglish ? 'translateX(0%)' : 'translateX(100%)',
                      zIndex: 1,
                    }}
                  />
                </button>
              </div>
            )}
            {/* Decorative floating sparkles */}
            <div className="absolute -top-6 sm:-top-10 left-1/2 -translate-x-1/2 w-16 sm:w-32 h-16 sm:h-32 bg-gradient-to-br from-yellow-200 via-pink-200 to-blue-200 rounded-full blur-2xl opacity-30 z-0 animate-float" />
            <div className="absolute top-4 sm:top-10 right-4 sm:right-10 w-8 sm:w-16 h-8 sm:h-16 bg-gradient-to-br from-pink-200 via-blue-100 to-purple-100 rounded-full blur-2xl opacity-30 z-0 animate-float-slow" />
            <div className="absolute bottom-4 sm:bottom-10 left-4 sm:left-10 w-10 sm:w-20 h-10 sm:h-20 bg-gradient-to-br from-blue-200 via-purple-200 to-pink-200 rounded-full blur-2xl opacity-30 z-0 animate-float-slower" />
            {/* Modern SVG/emoji illustration */}
            <div className="flex justify-center mb-4 sm:mb-6 z-10">
              <span className="text-5xl sm:text-7xl md:text-8xl drop-shadow-lg">📖✨</span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-2 sm:mb-4 drop-shadow-2xl tracking-tight z-10 animate-glow leading-tight md:leading-[1.15]">Welcome to Quicklish</h1>
            <p className="text-lg sm:text-2xl md:text-3xl text-gray-900 mb-2 font-semibold z-10 max-w-xl sm:max-w-2xl mx-auto animate-fadein">
              {memoizedTranslations.heroSubtitle}
            </p>
            <div className="mb-4 sm:mb-6 z-10">
              <span className="inline-block text-base sm:text-lg md:text-2xl font-bold bg-gradient-to-r from-yellow-400 via-pink-400 to-blue-400 bg-clip-text text-transparent tracking-wide px-4 sm:px-6 py-1 sm:py-2 rounded-full shadow-md animate-fadein">
                {memoizedTranslations.exploreFeatures}
              </span>
            </div>
          </div>
        </section>
        {/* Feature Sections */}
        {FEATURE_GROUPS.map((group, i) => (
          <section
            key={group.group}
            className={`relative flex flex-col md:flex-row ${i % 2 === 1 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-6 sm:gap-12 py-8 sm:py-16 px-2 sm:px-6 mb-6 sm:mb-12 rounded-2xl sm:rounded-3xl shadow-2xl border-2 sm:border-4 border-yellow-300/80 animate-fade-in overflow-hidden`}
          >
            {/* Gradient accent bar */}
            <div className={`absolute ${i % 2 === 1 ? 'left-0' : 'right-0'} top-4 sm:top-8 bottom-4 sm:bottom-8 w-1 sm:w-3 rounded-full bg-gradient-to-b ${i === 0 ? 'from-blue-400 via-purple-400 to-pink-400' :
              i === 1 ? 'from-purple-400 via-pink-400 to-blue-400' :
                i === 2 ? 'from-pink-400 via-blue-400 to-yellow-300' :
                  i === 3 ? 'from-yellow-300 via-pink-400 to-blue-400' :
                    'from-blue-400 via-purple-400 to-pink-400'
              } opacity-80`}></div>
            {/* Text column */}
            <div className="flex-1 flex flex-col items-center md:items-start justify-center bg-white/80 rounded-xl sm:rounded-2xl p-4 sm:p-8 z-10">
              <div className="flex flex-col items-center md:items-start w-full">
                <div className="text-4xl sm:text-6xl mb-2 sm:mb-4 drop-shadow-xl text-center md:text-left w-full">{featureIcons[i]}</div>
                <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-1 sm:mb-2 drop-shadow-xl text-center md:text-left w-full">{featureTitles[i]}</h2>
                {/* Remove the hardcoded featureDescriptions line */}
                {/* Feature description for the first section, using translations and showing selected language */}
                {i === 0 ? (
                  <div className="mb-2 sm:mb-4 w-full">
                    {/* Remove language code display */}
                    <div className="text-base sm:text-lg text-gray-900 font-semibold text-center md:text-left w-full">
                      <div className="mb-2">{memoizedTranslations.unlockTitle}</div>
                      <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
                        <li>{memoizedTranslations.unlockList1}</li>
                        <li>{memoizedTranslations.unlockList2}</li>
                        <li>{memoizedTranslations.unlockList3}</li>
                        <li>{memoizedTranslations.unlockList4}</li>
                        <li>{memoizedTranslations.unlockList5}</li>
                        <li>{memoizedTranslations.unlockList6}</li>
                        <li>{memoizedTranslations.unlockList7}</li>
                        <li>{memoizedTranslations.unlockList8}</li>
                        <li>{memoizedTranslations.unlockList9}</li>
                      </ul>
                    </div>
                  </div>
                ) : null}
                {/* Feature description for the AI Teacher section */}
                {i === 1 ? (
                  <div className="mb-2 sm:mb-4 w-full">
                    <div className="text-base sm:text-lg text-gray-900 font-semibold text-center md:text-left w-full">
                      <div className="mb-2">{memoizedTranslations.aiTeacherTitle}</div>
                      <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
                        <li><strong>{memoizedTranslations.aiTeacherRealTimeGrammar.split(':')[0]}:</strong> {memoizedTranslations.aiTeacherRealTimeGrammar.split(':')[1]}</li>
                        <li><strong>{memoizedTranslations.aiTeacherConversationalPractice.split(':')[0]}:</strong> {memoizedTranslations.aiTeacherConversationalPractice.split(':')[1]}</li>
                        <li><strong>{memoizedTranslations.aiTeacherPersonalizedLearning.split(':')[0]}:</strong> {memoizedTranslations.aiTeacherPersonalizedLearning.split(':')[1]}</li>
                        <li><strong>{memoizedTranslations.aiTeacherEncouragingSupport.split(':')[0]}:</strong> {memoizedTranslations.aiTeacherEncouragingSupport.split(':')[1]}</li>
                        <li><strong>{memoizedTranslations.aiTeacherVoiceRecognition.split(':')[0]}:</strong> {memoizedTranslations.aiTeacherVoiceRecognition.split(':')[1]}</li>
                        <li><strong>{memoizedTranslations.aiTeacher3DAvatar.split(':')[0]}:</strong> {memoizedTranslations.aiTeacher3DAvatar.split(':')[1]}</li>
                      </ul>
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <div className="text-sm font-semibold text-blue-800 mb-1">{memoizedTranslations.aiTeacherExampleConversation}</div>
                        <div className="text-xs text-gray-700">
                          <div><strong>You:</strong> I going to store</div>
                          <div><strong>AI Teacher:</strong> Actually, it should be 'I am going to the store' instead of 'I going to store'. You need 'am' for present continuous tense and 'the' article before 'store'. What do you need to buy at the store?</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                {/* Feature description for the memorize section, using translations and showing selected language */}
                {i === 2 ? (
                  <div className="mb-2 sm:mb-4 w-full">
                    {/* Remove language code display */}
                    <div className="text-base sm:text-lg text-gray-900 font-semibold text-center md:text-left w-full">
                      <div className="mb-2">{memoizedTranslations.memorizeTitle}</div>
                      <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
                        <li>{memoizedTranslations.memorizeDesc1}</li>
                        <li>{memoizedTranslations.memorizeDesc2}</li>
                        <li>{memoizedTranslations.memorizeDesc3}</li>
                        <li>{memoizedTranslations.memorizeDesc4}</li>
                        <li>{memoizedTranslations.memorizeDesc5}</li>
                        <li>{memoizedTranslations.memorizeDesc6}</li>
                        <li>{memoizedTranslations.memorizeDesc7}</li>
                        <li>{memoizedTranslations.memorizeDesc8}</li>
                      </ul>
                    </div>
                  </div>
                ) : null}
                {/* Feature description for the favorite section, using translations and showing selected language */}
                {i === 3 ? (
                  <div className="mb-2 sm:mb-4 w-full">
                    {/* Remove language code display */}
                    <div className="text-base sm:text-lg text-gray-900 font-semibold text-center md:text-left w-full">
                      <div className="mb-2">{memoizedTranslations.favoriteTitle}</div>
                      <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
                        <li>{memoizedTranslations.favoriteDesc1}</li>
                        <li>{memoizedTranslations.favoriteDesc2}</li>
                        <li>{memoizedTranslations.favoriteDesc3}</li>
                      </ul>
                    </div>
                  </div>
                ) : null}
                {/* Feature description for the completed section, using translations and showing selected language */}
                {i === 4 ? (
                  <div className="mb-2 sm:mb-4 w-full">
                    {/* Remove language code display */}
                    <div className="text-base sm:text-lg text-gray-900 font-semibold text-center md:text-left w-full">
                      <div className="mb-2">{memoizedTranslations.completedTitle}</div>
                      <ul className="list-disc list-inside mb-2 text-base text-gray-800 pl-4">
                        <li>{memoizedTranslations.completedDesc1}</li>
                        <li>{memoizedTranslations.completedDesc2}</li>
                        <li>{memoizedTranslations.completedDesc3}</li>
                        <li>{memoizedTranslations.completedDesc4}</li>
                      </ul>
                    </div>
                  </div>
                ) : null}
                {/* Remove the featureSubFeatures list items */}
                {/* <ul className="space-y-1 sm:space-y-2 text-yellow-700/90 text-sm sm:text-base mb-4 sm:mb-6 w-full max-w-xs sm:max-w-lg mx-auto text-center md:text-left">
                  {i === 0 && featureSubFeatures[i].map((item, idx) => {
                    // For the new Conversations link, use the correct route
                    if (item.startsWith('Conversations:')) {
                      return (
                        <li key={idx} className="text-center md:text-left">
                          <Link
                            to="/conversations"
                            className="text-blue-700 hover:text-pink-600 font-semibold underline underline-offset-2 transition-colors duration-150 cursor-pointer"
                          >
                            {item}
                          </Link>
                        </li>
                      );
                    }
                    // For the other links, use the original logic
                    return (
                      <li key={idx} className="text-center md:text-left">
                        <Link
                          to={group.features[idx]?.route}
                          className="text-blue-700 hover:text-pink-600 font-semibold underline underline-offset-2 transition-colors duration-150 cursor-pointer"
                        >
                          {item}
                        </Link>
                      </li>
                    );
                  })}
                </ul> */}
              </div>
            </div>
            {/* Content column */}
            <div className="flex-1 flex flex-col items-center justify-center z-10 w-full">
              {/* Special handling for AI Teacher section */}
              {i === 1 ? (
                <>
                  {/* AI Teacher Example Card */}
                  <div className="w-full max-w-md bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border-2 border-purple-200/60 p-6 mb-6">
                    <div className="text-center mb-4">
                      <div className="text-3xl mb-2">🎓</div>
                      <h3 className="text-xl font-bold text-purple-700 mb-2">Professor Alex</h3>
                      <div className="text-sm text-gray-600 mb-4">Your AI English Tutor</div>
                    </div>

                    {/* Conversation Example */}
                    <div className="space-y-3 mb-4">
                      <div className="bg-blue-100 rounded-lg p-3">
                        <div className="text-xs text-blue-600 font-semibold mb-1">You:</div>
                        <div className="text-sm text-gray-800">"I have car"</div>
                      </div>
                      <div className="bg-purple-100 rounded-lg p-3">
                        <div className="text-xs text-purple-600 font-semibold mb-1">Professor Alex:</div>
                        <div className="text-sm text-gray-800">
                          "Actually, it should be <strong>'I have a car'</strong> instead of <em>'I have car'</em>. You need the article 'a' before 'car'. That's great! Do you drive your car often?"
                        </div>
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="text-gray-700">Grammar correction</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="text-gray-700">Voice interaction</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="text-gray-700">3D avatar</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="text-gray-700">Real-time feedback</span>
                      </div>
                    </div>
                  </div>

                  {/* AI Teacher Action Button */}
                  <Link
                    to="/robo-teacher"
                    className="w-full sm:w-auto flex flex-row items-center justify-center rounded-full shadow-2xl bg-gradient-to-r from-purple-500 via-pink-400 to-blue-400 border border-purple-400 sm:border-2 transition-all duration-200 group hover:shadow-purple-400/40 hover:border-pink-400 focus:outline-none focus:ring-4 focus:ring-purple-200/40 magical-btn px-6 sm:px-8 py-3 sm:py-4 text-lg sm:text-xl font-extrabold text-white hover:text-yellow-200 text-center mb-0 backdrop-blur-md cursor-pointer"
                    style={{ minWidth: 0, minHeight: 50, maxWidth: 250 }}
                  >
                    <span className="text-2xl sm:text-3xl mr-2 sm:mr-3 drop-shadow-lg transition-colors duration-200 group-hover:text-yellow-200">🤖</span>
                    <span className="tracking-tight leading-tight transition-colors duration-200 group-hover:text-yellow-200">Start Learning with AI</span>
                    <span className="ml-2 sm:ml-3 text-2xl sm:text-3xl transition-colors duration-200 group-hover:text-yellow-200">→</span>
                  </Link>
                </>
              ) : (
                <>
                  {/* Example cards (ItemCard) for each feature */}
                  <div className="w-full flex flex-col md:flex-row gap-3 sm:gap-4 justify-center items-center md:items-start mb-4 sm:mb-8">
                    {group.features.map((feature, idx) => {
                      let type: 'grammar' | 'sentence' | 'word' = 'grammar';
                      let item = sampleGrammar;
                      let showMemorizeAgain = false;
                      let showAddToFavorite = false;
                      let isFavorite = false;
                      let hideActions = false;
                      if (feature.title.toLowerCase().includes('sentence')) {
                        type = 'sentence';
                        item = sampleSentence;
                      } else if (feature.title.toLowerCase().includes('word')) {
                        type = 'word';
                        item = sampleWord;
                      }
                      // Button logic by section
                      if (group.group === 'Memorize') {
                        showAddToFavorite = true;
                        showMemorizeAgain = true;
                        isFavorite = false;
                      } else if (group.group === 'Favorites') {
                        showAddToFavorite = true;
                        showMemorizeAgain = false;
                        isFavorite = true; // show as already favorited (Unfavorite)
                      } else if (group.group === 'Completed') {
                        hideActions = true;
                      } else {
                        // Default for Learn English and any other
                        showAddToFavorite = true;
                        showMemorizeAgain = false;
                        isFavorite = false;
                        hideActions = false;
                      }
                      return (
                        <div key={feature.title} className="w-full max-w-xs md:max-w-[280px] min-h-[220px] sm:min-h-[240px] max-h-[400px] flex flex-col items-stretch">
                          <ItemCard
                            item={item}
                            type={type}
                            onDone={() => { }}
                            onMemorize={() => { }}
                            onFavorite={() => { }}
                            showMemorizeAgain={showMemorizeAgain}
                            showAddToFavorite={showAddToFavorite}
                            selectedLanguage={selectedLanguage}
                            isFavorite={isFavorite}
                            hideActions={hideActions}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {/* Stylish buttons for all sections */}
                  <div className="w-full flex flex-col sm:flex-row gap-2 sm:gap-5 justify-center items-center md:items-stretch mt-0 sm:mt-4 mb-2 sm:mb-4">
                    {group.features.map((feature, idx) => {
                      // After Word Practice, add Conversations button
                      const isWordPractice = feature.title === 'Word Practice';
                      return (
                        <React.Fragment key={feature.title}>
                          <Link
                            to={feature.route}
                            className="w-full sm:w-auto flex flex-row items-center justify-center rounded-full shadow-2xl bg-gradient-to-r from-blue-500 via-purple-400 to-pink-400 border border-blue-400 sm:border-2 transition-all duration-200 group hover:shadow-blue-400/40 hover:border-pink-400 focus:outline-none focus:ring-4 focus:ring-blue-200/40 magical-btn px-4 sm:px-7 py-2 sm:py-3 text-base sm:text-lg font-extrabold text-white hover:text-yellow-200 text-center mb-0 backdrop-blur-md cursor-pointer"
                            style={{ minWidth: 0, minHeight: 44, maxWidth: 200 }}
                          >
                            <span className="text-xl sm:text-2xl mr-1 sm:mr-2 drop-shadow-lg transition-colors duration-200 group-hover:text-yellow-200">{feature.icon}</span>
                            <span className="tracking-tight leading-tight transition-colors duration-200 group-hover:text-yellow-200">{feature.title}</span>
                            <span className="ml-1 sm:ml-2 text-xl sm:text-2xl transition-colors duration-200 group-hover:text-yellow-200">→</span>
                          </Link>
                          {isWordPractice && group.group === 'Learn English' && (
                            <Link
                              to="/conversations"
                              className="w-full sm:w-auto flex flex-row items-center justify-center rounded-full shadow-2xl bg-gradient-to-r from-blue-500 via-purple-400 to-pink-400 border border-blue-400 sm:border-2 transition-all duration-200 group hover:shadow-blue-400/40 hover:border-pink-400 focus:outline-none focus:ring-4 focus:ring-blue-200/40 magical-btn px-4 sm:px-7 py-2 sm:py-3 text-base sm:text-lg font-extrabold text-white hover:text-yellow-200 text-center mb-0 backdrop-blur-md cursor-pointer"
                              style={{ minWidth: 0, minHeight: 44, maxWidth: 200 }}
                            >
                              <span className="text-xl sm:text-2xl mr-1 sm:mr-2 drop-shadow-lg transition-colors duration-200 group-hover:text-yellow-200">💬</span>
                              <span className="tracking-tight leading-tight transition-colors duration-200 group-hover:text-yellow-200">Conversations</span>
                              <span className="ml-1 sm:ml-2 text-xl sm:text-2xl transition-colors duration-200 group-hover:text-yellow-200">→</span>
                            </Link>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {/* Stylish progress bars only for Learn English */}
                  {group.group === 'Learn English' && (
                    <div className="w-full flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 mt-2 sm:mt-4">
                      <div className="flex flex-col items-center w-full max-w-sm px-2 mb-3 md:mb-0">
                        <ProgressBarFancy label="Grammar" completed={progress.grammar.completed} total={progress.grammar.total} isLoading={isLoadingProgress} />
                      </div>
                      <div className="flex flex-col items-center w-full max-w-sm px-2 mb-3 md:mb-0">
                        <ProgressBarFancy label="Sentences" completed={progress.sentences.completed} total={progress.sentences.total} isLoading={isLoadingProgress} />
                      </div>
                      <div className="flex flex-col items-center w-full max-w-sm px-2">
                        <ProgressBarFancy label="Words" completed={progress.words.completed} total={progress.words.total} isLoading={isLoadingProgress} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        ))}
        {/* Test your progress Section */}
        <section className="max-w-2xl sm:max-w-5xl mx-auto px-2 sm:px-4 py-8 sm:py-16 mb-6 sm:mb-12 bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl border-2 sm:border-4 border-blue-300/80 animate-fade-in">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4 sm:mb-8 text-center drop-shadow-xl">
            Test Your Progress <span className="ml-2 text-base sm:text-lg font-semibold text-blue-400">(Coming Soon)</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
            {/* Grammar Test Card */}
            <div className="relative flex flex-col items-center bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-blue-200/60">
              <div className="absolute top-2 right-2 bg-yellow-400 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full shadow">Coming Soon</div>
              <div className="text-2xl sm:text-4xl mb-1 sm:mb-2 drop-shadow-xl">📘</div>
              <div className="text-lg sm:text-xl font-bold text-blue-700 mb-1">
                Grammar Test
              </div>
              <div className="text-gray-900 text-sm sm:text-base text-center font-semibold mb-1 sm:mb-2">
                Identify Nouns
              </div>
              <div className="text-indigo-700 font-bold mb-1 sm:mb-2">"Ram is a boy."</div>
              <div className="flex flex-row gap-1 sm:gap-2 mt-1 sm:mt-2">
                <span className="bg-blue-200 text-blue-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Beginner
                </span>
                <span className="bg-purple-200 text-purple-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Intermediate
                </span>
                <span className="bg-pink-200 text-pink-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Advanced
                </span>
              </div>
            </div>
            {/* Sentences Test Card */}
            <div className="relative flex flex-col items-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-purple-200/60">
              <div className="absolute top-2 right-2 bg-yellow-400 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full shadow">Coming Soon</div>
              <div className="text-2xl sm:text-4xl mb-1 sm:mb-2 drop-shadow-xl">✍️</div>
              <div className="text-lg sm:text-xl font-bold text-purple-700 mb-1">
                Sentence Test
              </div>
              <div className="text-gray-900 text-sm sm:text-base text-center font-semibold mb-1 sm:mb-2">
                Translate to English
              </div>
              <div className="text-indigo-700 font-bold mb-1 sm:mb-2">"She runs fast"</div>
              <div className="flex flex-row gap-1 sm:gap-2 mt-1 sm:mt-2">
                <span className="bg-blue-200 text-blue-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Beginner
                </span>
                <span className="bg-purple-200 text-purple-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Intermediate
                </span>
                <span className="bg-pink-200 text-pink-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Advanced
                </span>
              </div>
            </div>
            {/* Vocabulary Test Card */}
            <div className="relative flex flex-col items-center bg-gradient-to-br from-pink-100 via-blue-100 to-purple-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-pink-200/60">
              <div className="absolute top-2 right-2 bg-yellow-400 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full shadow">Coming Soon</div>
              <div className="text-2xl sm:text-4xl mb-1 sm:mb-2 drop-shadow-xl">🧠</div>
              <div className="text-lg sm:text-xl font-bold text-pink-700 mb-1">
                Vocabulary Test
              </div>
              <div className="text-gray-900 text-sm sm:text-base text-center font-semibold mb-1 sm:mb-2">
                What is the meaning?
              </div>
              <div className="text-indigo-700 font-bold mb-1 sm:mb-2">"Benevolent"</div>
              <div className="flex flex-row gap-1 sm:gap-2 mt-1 sm:mt-2">
                <span className="bg-blue-200 text-blue-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Beginner
                </span>
                <span className="bg-purple-200 text-purple-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Intermediate
                </span>
                <span className="bg-pink-200 text-pink-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-semibold">
                  Advanced
                </span>
              </div>
            </div>
          </div>
        </section>
        {/* Why My English Gurukul Section */}
        <section className="max-w-2xl sm:max-w-5xl mx-auto px-2 sm:px-4 py-8 sm:py-16 mb-6 sm:mb-12 bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl border-2 sm:border-4 border-yellow-300/80 animate-fade-in">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4 sm:mb-6 drop-shadow-xl">
              {memoizedTranslations.whyGuruTitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-blue-200/40 hover:shadow-xl transition-all duration-300">
                <div className="text-3xl mb-4">🚀</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Fast Progress</h3>
                <p className="text-gray-600 text-sm">
                  {memoizedTranslations.whyGuruDesc1}
                </p>
              </div>
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-blue-200/40 hover:shadow-xl transition-all duration-300">
                <div className="text-3xl mb-4">🌏</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Multi-Language</h3>
                <p className="text-gray-600 text-sm">
                  {memoizedTranslations.whyGuruDesc2}
                </p>
              </div>
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-blue-200/40 hover:shadow-xl transition-all duration-300">
                <div className="text-3xl mb-4">📈</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Track Progress</h3>
                <p className="text-gray-600 text-sm">
                  {memoizedTranslations.whyGuruDesc3}
                </p>
              </div>
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-blue-200/40 hover:shadow-xl transition-all duration-300">
                <div className="text-3xl mb-4">💡</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Smart Practice</h3>
                <p className="text-gray-600 text-sm">
                  {memoizedTranslations.whyGuruDesc4}
                </p>
              </div>
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-blue-200/40 hover:shadow-xl transition-all duration-300">
                <div className="text-3xl mb-4">🔒</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Safe & Private</h3>
                <p className="text-gray-600 text-sm">
                  {memoizedTranslations.whyGuruDesc5}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Footer */}
      <footer className="w-full bg-gradient-to-r from-yellow-100 via-pink-100 to-blue-100 backdrop-blur-2xl py-4 sm:py-8 px-2 sm:px-4 flex flex-col md:flex-row items-center justify-between gap-2 sm:gap-4 border-t-2 sm:border-t-4 border-yellow-300/80">
        <div className="flex flex-wrap gap-2 sm:gap-4 items-center justify-center text-xs sm:text-base text-yellow-700 font-medium">
          <Link to="/about" className="hover:underline">
            About Us
          </Link>
          <Link to="/help" className="hover:underline">
            Help & Support
          </Link>
          <Link to="/privacy" className="hover:underline">
            Privacy Policy
          </Link>
          <Link to="/community" className="hover:underline">
            Community
          </Link>
          <Link to="/delete-account" className="hover:underline">
            Delete Account
          </Link>
        </div>
        <div className="text-[10px] sm:text-sm text-yellow-700 text-center md:text-right">
          © {new Date().getFullYear()} Quicklish. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default HomePage;